import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import { DepartmentType } from "./departmentsApi";

export interface DepartmentsState {
  departmentsList: DepartmentType[];
  currentDepartment: DepartmentType | null;
  currentDepartmentUuid: string | null;
  currentDepartmentStatus: QueryStatus;
  getDepartmentsByManagerStatus: QueryStatus;
  loadDepartmentStatus: QueryStatus;
}

const initialState: DepartmentsState = {
  departmentsList: [],
  currentDepartment: null,
  currentDepartmentUuid: null,
  currentDepartmentStatus: "idle",
  getDepartmentsByManagerStatus: "idle",
  loadDepartmentStatus: "idle",
};

export const getDepartmentsByManagerAsync = createAsyncThunk(
  "departments/getDepartmentsByManager",
  async (payload: { departmentUuid: string }, thunkAPI) => {
    const manager = (thunkAPI.getState() as RootState).home.manager;
    const axios = authAxios({
      departmentUuid: payload.departmentUuid,
    });
    const response = await axios.get<DepartmentType[]>(
      `departments/departments/manager/${manager?.uuid}`,
    );

    return response.data;
  },
);

export const loadDepartmentAsync = createAsyncThunk(
  "departments/loadDepartment",
  async (payload: { departmentUuid: string }) => {
    const axios = authAxios({
      departmentUuid: payload.departmentUuid,
    });
    const response = await axios.get<DepartmentType>(
      `departments/department/${payload.departmentUuid}`,
    );

    return response.data;
  },
);

export const departmentsSlice = createSlice({
  name: "departments",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    getCurrentDepartment: (state) => {
      const departmentUuid = localStorage.getItem("department");
      state.currentDepartmentUuid = departmentUuid;
      state.currentDepartmentStatus = "success";
    },
    setCurrentDepartment: (
      state,
      action: PayloadAction<{ departmentUuid: string }>,
    ) => {
      localStorage.setItem("department", action.payload.departmentUuid);
      state.currentDepartmentUuid = action.payload.departmentUuid;
      state.currentDepartmentStatus = "success";
    },
    removeCurrentDepartment: (state) => {
      localStorage.removeItem("department");
      state.currentDepartmentStatus = "idle";
      state.currentDepartmentUuid = null;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentsByManagerAsync.pending, (state) => {
        state.getDepartmentsByManagerStatus = "processing";
      })
      .addCase(getDepartmentsByManagerAsync.fulfilled, (state, action) => {
        state.getDepartmentsByManagerStatus = "success";
        state.departmentsList = action.payload;
      })
      .addCase(getDepartmentsByManagerAsync.rejected, (state) => {
        state.getDepartmentsByManagerStatus = "failed";
      })
      .addCase(loadDepartmentAsync.pending, (state) => {
        state.loadDepartmentStatus = "processing";
      })
      .addCase(loadDepartmentAsync.fulfilled, (state, action) => {
        state.currentDepartment = action.payload;
        state.loadDepartmentStatus = "success";
      })
      .addCase(loadDepartmentAsync.rejected, (state) => {
        state.loadDepartmentStatus = "failed";
      });
  },
});

export const {
  getCurrentDepartment,
  setCurrentDepartment,
  removeCurrentDepartment,
} = departmentsSlice.actions;

export const selectGetDepartmentsByManagerStatus = (state: RootState) =>
  state.departmentsForDepartmentUser.getDepartmentsByManagerStatus;

export const selectDepartmentsList = (state: RootState) =>
  state.departmentsForDepartmentUser.departmentsList;

export const selectCurrentDepartment = (state: RootState) =>
  state.departmentsForDepartmentUser.currentDepartment;

export const selectCurrentDepartmentUuid = (state: RootState) =>
  state.departmentsForDepartmentUser.currentDepartmentUuid;

export const selectCurrentDepartmentStatus = (state: RootState) =>
  state.departmentsForDepartmentUser.currentDepartmentStatus;

export const selectLoadDepartmentStatus = (state: RootState) =>
  state.departmentsForDepartmentUser.loadDepartmentStatus;

export default departmentsSlice.reducer;
